import { useState, useEffect, useCallback } from "react";
import UsersTable from "./UsersTable";
import UserCard from "./UserCard";

import Modal from "../../components/common/Modal";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import StatusScreen from "../../components/common/StatusScreen";

import AddCustomerForm from "../../components/users/AddCustomerForm";
import TableLoader from "../../components/loaders/TableLoader";
import axios from "axios";

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showStatus, setShowStatus] = useState(false);

  const [pendingData, setPendingData] = useState(null);
  const [statusType, setStatusType] = useState("loading");
  const [statusMessage, setStatusMessage] = useState("");
  const [loadingPage, setLoadingPage] = useState(true);

  /* =========================================================================
     FETCH CUSTOMERS — wrapped in useCallback to keep stable reference
     ======================================================================== */
  const loadCustomers = useCallback(async () => {
    console.log("🔄 Refreshing customers...");

    try {
      const res = await axios.get(
        process.env.REACT_APP_BACKEND_BASE_URL + "/api/v1/fetch/users/all",
        { params: { userType: "CUSTOMER" }, timeout: 5000 }
      );

      if (res.data?.data) {
        const mapped = res.data.data.map((c) => ({
          name: `${c.firstName} ${c.lastName}`,
          email: c.email,
          phone: c.phoneNumber,
          location: c.location,
          identityId: c.identityId,
          role: "Customer",
          status: c.status === "ACTIVE" ? "Active" : "Inactive",
          tractors: c.tractors || [],
        }));

        setCustomers(mapped);
        setLastUpdated(new Date().toLocaleTimeString());
        console.log("✅ Customer refresh success");
        if (loadingPage) setLoadingPage(false);
      }
    } catch (err) {
      console.error("❌ Failed to refresh customers:", err.message);
      if (loadingPage) setLoadingPage(false);
    }
  }, [loadingPage]);

  /* =========================================================================
     AUTO-REFRESH — guaranteed to work
     ======================================================================== */
  useEffect(() => {
    loadCustomers(); // initial fetch

    const interval = setInterval(() => {
      loadCustomers();
    }, process.env.REACT_APP_REFRESH_AFTER);

    return () => clearInterval(interval);
  }, [loadCustomers]);

  /* =========================================================================
     HANDLE FORM SUBMISSION (No backend save here – handled in AddCustomerForm)
     ======================================================================== */
  const handleFormSubmit = (data) => {
    setPendingData(data);
    setShowModal(false);
    setShowConfirm(true);
  };

  /* =========================================================================
     CONFIRMATION LOGIC (your simulated save)
     ======================================================================== */
  const confirmSave = async () => {
    setShowConfirm(false);

    setStatusType("loading");
    setStatusMessage("Saving customer...");
    setShowStatus(true);

    await new Promise((res) => setTimeout(res, 1800));

    // Local UI update (backend auto-refresh will overwrite soon)
    setCustomers((prev) => [
      ...prev,
      {
        name: `${pendingData.firstName} ${pendingData.lastName}`,
        email: pendingData.email,
        phone: pendingData.phone,
        role: "Customer",
        status: "Active",
      },
    ]);

    setStatusType("success");
    setStatusMessage("Customer added successfully!");

    setTimeout(() => setShowStatus(false), 1500);
  };

  /* ========================================================================= */

  return (
    <div className="dashboard-content min-h-full text-gray-900 dark:text-gray-100">
      {/* Page Loading */}
      {loadingPage && <TableLoader />}

      {!loadingPage && (
        <>
          <div className="dashboard-grid dashboard-grid-1 mb-6">
            <UserCard
              title="Total Customers"
              count={customers.length}
              color="#F3B14E"
              icon="🚜"
            />
          </div>

          <UsersTable
            data={customers}
            title="Customer"
            onAddUser={() => setShowModal(true)}
            onViewUser={(user) => alert(JSON.stringify(user, null, 2))}
          />
        </>
      )}

      <Modal
        open={showModal}
        title="Add Customer"
        icon="🚜"
        onClose={() => setShowModal(false)}
      >
        <AddCustomerForm onSubmit={handleFormSubmit} onClose={() => setShowModal(false)} />
      </Modal>

      <ConfirmDialog
        open={showConfirm}
        message="Are you sure you want to save this customer?"
        onConfirm={confirmSave}
        onCancel={() => setShowConfirm(false)}
      />

      <StatusScreen
        open={showStatus}
        status={statusType}
        message={statusMessage}
      />
    </div>
  );
}